
import CommonMixin from '@/core/pages/CommonMixin';
import ConfirmationDialog from '@/core/components/ConfirmationDialog.vue';
import * as TelliqService from '@/services/telliq.service';
import { CREATE_BOOKING_DATE_FORMAT, CREATE_BOOKING_TIME_FORMAT, emitter } from '@/utils/constants';
import moment from 'moment';
import MainButton from '@/core/components/MainButton.vue';
import Component from 'vue-class-component';
import { Booking, CreateBooking, Machine } from '@/models/bookings';
import { MUTATIONS } from '@/store/mutations';
import { Driver } from '@/models/users';
import { confirmationDialogText } from '@/utils/functions';

@Component({
  components: {
    MainButton,
    ConfirmationDialog,
  },
})
export default class CheckInBooking extends CommonMixin {
  active_booking?: Booking;
  driver?: Driver;
  machine?: Machine;
  overlapping_booking?: Booking;
  show_dialog = false;
  confirmation_dialog_text = { title: '', text: '' };

  async created(): Promise<void> {
    try {
      this.loading = true;

      const machine_id = +this.$route.params.id;
      const start_date = moment().subtract(15, 'days').toISOString();
      const end_date = moment().endOf('day').add(15, 'days').toISOString();

      const [machines, bookings] = await Promise.all([
        TelliqService.getMachines(),
        TelliqService.getBookings(start_date, end_date, [machine_id]),
      ]);

      this.machine = machines.find((machine) => machine.id === machine_id);
      if (!this.machine) {
        emitter.emit('notify', {
          text: this.$t('bookings.snackbars.vehicle_not_found'),
          color: 'error',
        });
        return;
      }

      this.$store.commit(MUTATIONS.SET_HEADER, {
        title: this.$t('bookings.labels.car_name'),
        subtitle: this.machine.name,
      });

      const overlapping_booking = this.findOverlappingBooking(bookings);
      if (overlapping_booking) {
        this.active_booking = overlapping_booking;

        // if the booking is done by this driver, redirect for check-out.
        if (this.$store.state.user.id === overlapping_booking.driverId) {
          await this.$router.push({ path: '/booking/edit/' + overlapping_booking.id, query: { checkout: 'confirm' } });
          return;
        }

        // show who else has booked the car.
        const drivers = await TelliqService.getDrivers();
        this.driver = drivers.find((item) => item.driver.id === overlapping_booking.driverId);
        return;
      }

      await this.createAndRedirectToCarDetails();
    } catch (e) {
      emitter.emit('notify', {
        text: this.$t('core.errors.something_went_wrong'),
        color: 'error',
      });
    } finally {
      this.loading = false;
    }
  }

  async createAndRedirectToCarDetails(): Promise<void> {
    const start_date = moment();
    const end_date = moment().endOf('day');

    const create_booking: CreateBooking = {
      startDate: start_date.format(CREATE_BOOKING_DATE_FORMAT),
      startTime: start_date.format(CREATE_BOOKING_TIME_FORMAT),
      endDate: end_date.format(CREATE_BOOKING_DATE_FORMAT),
      endTime: end_date.format(CREATE_BOOKING_TIME_FORMAT),
      machine: this.machine,
    };
    this.$store.commit(MUTATIONS.SET_CREATE_BOOKING, create_booking);
    this.$store.commit(MUTATIONS.SET_INSTANT_BOOKING, true);
    // This is a workaround to enable date and time picker colours.
    // this.$store.commit(MUTATIONS.SET_EDIT_BOOKING, '');
    // Vehicle is available for THIS TIMEFRAME
    create_booking.canBePrivate = this.machine?.isPrivateBookingEnabled;
    this.$store.commit(MUTATIONS.SET_CREATE_BOOKING, create_booking);

    await this.$router.push('/booking/car-selection-details');
  }

  findOverlappingBooking(bookings: Booking[]): Booking | undefined {
    return bookings.find((booking) => moment().isBetween(booking.startDate, booking.endDate));
  }

  get lastBookingTime(): string {
    return this.active_booking ? moment(this.active_booking.endDate).format('DD.MM.YYYY HH:mm') : '';
  }

  get lastBookingDriver(): string {
    return this.driver?.driver.name ?? '';
  }

  canBeCheckedOut(): boolean {
    return moment().utcOffset(0, true).isBefore(this.active_booking?.endDate);
  }

  onCheckOutClick(): void {
    this.show_dialog = true;
    this.confirmation_dialog_text = confirmationDialogText().check_out;
  }

  async onDialogResponse(response: boolean): Promise<void> {
    if (response) {
      await this.checkOutBooking();
    }
    this.show_dialog = false;
  }

  async checkOutBooking(): Promise<void> {
    if (!this.active_booking) {
      return;
    }

    try {
      await TelliqService.stopBooking(this.active_booking.id, this.active_booking);

      await this.createAndRedirectToCarDetails();
    } catch (e) {
      emitter.emit('notify', {
        text: this.$t('core.errors.something_went_wrong'),
        color: 'error',
      });
    }
  }
}
